/**
 * The Command Center's score history (blueprint §7 C6, C9, C28), built from
 * the lightweight history endpoints. The executive report used to supply it,
 * but that report runs an Azure OpenAI narrative on every uncached load and
 * has no server cache, so the Command Center no longer calls it (Adam,
 * 2026-10-05). Pure.
 *
 * Sources:
 *   - Best Practice: the overall row per day from
 *     `/compliance/tenant/{id}/scores/history?days=90`. useComplianceDashboard
 *     already loads that exact query key, so it adds no request.
 *   - Secure Score, MFA adoption and baseline: the daily metric snapshots from
 *     `/metrictrends/tenant/{id}`. The server returns 14 days
 *     (GetDashboardTrendsAsync uses GetTrendAsync's default), so these three
 *     lines are short and their tile deltas name their true span ("since Sep
 *     18"), not the 90-day window.
 *
 * The daily rows mirror ExecutiveReportService.BuildTrendDataPoints, so the
 * chart reads like the report's, with one difference. A day's Guardian Score
 * is set only when every component the live score uses has a value that day.
 * The server renormalizes the weights over whatever is present, so a day that
 * lacked one component would read as a jump.
 */
import type { Category } from '@/components/carbon'
import { calculateUnifiedGuardianScore } from '@/hooks/useComplianceData'
import type { DashboardTrends } from '@/types'
import type { ScoreDataPoint } from '@/types/executiveReport'
import type { ComplianceTrendPoint } from '@/types/standardsCompliance'
import { dayLabel, signed, type GuardianComponents } from './cc-score'
import { HISTORY_DAYS } from './cc-trend'

export { HISTORY_DAYS }

/** A delta over fewer days than this is too short to read as movement, so it is hidden. */
export const MIN_DELTA_SPAN_DAYS = 7

/**
 * How far short of the window a series may start and still read "in 90 days": one weekly sample of the chart,
 * so a missed evaluation day at the start of the window does not change the label.
 */
export const WINDOW_SLACK_DAYS = 7

export interface HistorySources {
  /** Best Practice: the overall score per day (getComplianceScoreHistory). */
  bpa: ReadonlyArray<ComplianceTrendPoint> | null | undefined
  /** Secure Score, MFA adoption and baseline snapshots (getDashboardTrends; null when none exist yet). */
  metrics: DashboardTrends | null | undefined
}

type ComponentField = 'baselineCompliance' | 'bpaScore' | 'mfaAdoption' | 'secureScore'

const FIELD: Record<Category, ComponentField> = {
  baseline: 'baselineCompliance',
  bpa: 'bpaScore',
  identity: 'mfaAdoption',
  secure: 'secureScore',
}

const DAY_MS = 86_400_000
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** "2026-10-02" from an ISO date or date-time (the UTC calendar day, as the server buckets it), or null. */
function dayKey(iso: unknown): string | null {
  const m = typeof iso === 'string' ? /^(\d{4}-\d{2}-\d{2})/.exec(iso) : null
  return m ? m[1] : null
}

const dayIndex = (key: string): number => Math.round(Date.parse(`${key}T00:00:00Z`) / DAY_MS)

/** One row per day with any component value, oldest first. */
export function composeScoreHistory(src: HistorySources, live: GuardianComponents): ScoreDataPoint[] {
  const rows = new Map<string, ScoreDataPoint>()
  const row = (key: string): ScoreDataPoint => {
    let r = rows.get(key)
    if (!r) {
      r = { date: `${key}T00:00:00Z`, guardianScore: null, secureScore: null, bpaScore: null, mfaAdoption: null, baselineCompliance: null }
      rows.set(key, r)
    }
    return r
  }
  const put = (iso: unknown, field: ComponentField, value: unknown) => {
    const key = dayKey(iso)
    const v = num(value)
    if (key && v != null) row(key)[field] = v
  }

  for (const p of src.bpa ?? []) put(p.date, 'bpaScore', p.overallScore)
  for (const p of src.metrics?.secureScore?.history ?? []) put(p.date, 'secureScore', p.value)
  for (const p of src.metrics?.mfaAdoption?.history ?? []) put(p.date, 'mfaAdoption', p.value)
  for (const p of src.metrics?.baselineCompliance?.history ?? []) put(p.date, 'baselineCompliance', p.value)

  const required = (Object.keys(FIELD) as Category[]).filter((k) => live[k] != null)
  const out = [...rows.values()].sort((a, b) => a.date.localeCompare(b.date))
  for (const r of out) {
    if (required.length === 0 || !required.every((k) => r[FIELD[k]] != null)) continue
    const pick = (k: Category) => (required.includes(k) ? r[FIELD[k]] : null)
    r.guardianScore = calculateUnifiedGuardianScore({
      secureScore: pick('secure'),
      bpaScore: pick('bpa'),
      mfaAdoption: pick('identity'),
      configCompliance: pick('baseline'),
    })
  }
  return out
}

/**
 * A pillar tile's delta tag (§7 C9): the newest value against the oldest one in
 * the window, in whole points as the chart draws them. The label names the true
 * span: "+4 in 90 days" when the series reaches back to the window's start
 * (within WINDOW_SLACK_DAYS, so 83 days or more), otherwise "+2 since Sep 18"
 * from its oldest point. Null when the series has under two points or spans
 * under MIN_DELTA_SPAN_DAYS.
 */
export function windowDelta(
  rows: ReadonlyArray<ScoreDataPoint>,
  category: Category,
  windowDays: number = HISTORY_DAYS,
): string | null {
  const field = FIELD[category]
  const pts = rows
    .map((r) => ({ key: dayKey(r.date), v: num(r[field]) }))
    .filter((p): p is { key: string; v: number } => p.key != null && p.v != null)
    .sort((a, b) => a.key.localeCompare(b.key))
  if (pts.length < 2) return null
  const last = pts[pts.length - 1]
  const inWindow = pts.filter((p) => dayIndex(last.key) - dayIndex(p.key) <= windowDays)
  const first = inWindow[0]
  const span = dayIndex(last.key) - dayIndex(first.key)
  if (inWindow.length < 2 || span < MIN_DELTA_SPAN_DAYS) return null
  const change = Math.round(last.v) - Math.round(first.v)
  const over = span >= windowDays - WINDOW_SLACK_DAYS ? `in ${windowDays} days` : `since ${dayLabel(first.key)}`
  return change === 0 ? `No change ${over}` : `${signed(change)} ${over}`
}

/** Every pillar tile's delta, hidden for a component the live score does not know (its tile shows no score). */
export function pillarDeltas(rows: ReadonlyArray<ScoreDataPoint>, live: GuardianComponents): Record<Category, string | null> {
  const delta = (k: Category) => (live[k] == null ? null : windowDelta(rows, k))
  return { baseline: delta('baseline'), bpa: delta('bpa'), identity: delta('identity'), secure: delta('secure') }
}
